import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Anchor, ExternalLink, Loader2 } from 'lucide-react';
import type { Page, PageSelectorProps } from '../ComponentEditModal.types';
import type { Component } from '../../../schemas/validation';
import { componentsApi } from '../../../api/client';
import { buildPageSectionHref, splitPageSectionHref } from '../../../utils/linkTargets';
import { useLinkTargetPageContext } from './LinkTargetPageContext';

/**
 * Link mode types for the PageSelector
 */
type LinkMode = 'page' | 'section' | 'custom';

/**
 * User-friendly display names for component types
 */
const COMPONENT_DISPLAY_NAMES: Record<string, string> = {
  hero: 'Hero Section',
  'split-hero': 'Hero Section',
  'simple-header': 'Header Section',
  features: 'Features Section',
  cta: 'Call to Action',
  pricing: 'Pricing Section',
  faq: 'FAQ Section',
  testimonials: 'Testimonials',
  team: 'Team Section',
  'contact-info': 'Contact Information',
  form: 'Contact Form',
  'media-text': 'Media & Text',
  'text-blocks': 'Text Content',
  video: 'Video Section',
  'image-gallery': 'Image Gallery',
  'image-info': 'Image with Info',
  'image-card': 'Image Cards',
  'countdown-timer': 'Countdown Timer',
  'title-subtitle': 'Title & Subtitle',
  footer: 'Footer',
  navbar: 'Navigation Bar',
  'html-embed': 'HTML / Embed',
  'html-split': 'Split HTML',
};

/**
 * Component types that should not appear in the section selector
 * (invisible/technical components or components that don't make sense as link targets)
 */
const INVISIBLE_TYPES = ['head-content', 'body-end', 'content-area', 'promo-banner'];

function filterLinkableComponents(components: Component[]): Component[] {
  return components
    .filter(c => c.visible !== false && !INVISIBLE_TYPES.includes(c.type))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

/** Layout header/footer + page-body sections in DOM order; dedupe by id (first wins). */
function mergeBodyWithLayoutSections(
  pageBodyComponents: Component[],
  layoutHeader: Component[] | undefined,
  layoutFooter: Component[] | undefined,
  includeLayoutChunks: boolean
): Component[] {
  const body = filterLinkableComponents(pageBodyComponents);
  if (!includeLayoutChunks) return body;

  const head = layoutHeader?.length ? filterLinkableComponents(layoutHeader) : [];
  const foot = layoutFooter?.length ? filterLinkableComponents(layoutFooter) : [];

  const combined = [...head, ...body, ...foot];
  const seen = new Set<string>();
  const out: Component[] = [];
  for (const c of combined) {
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    out.push(c);
  }
  return out;
}

function samePageId(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}

function samePageSlug(a?: string | null, b?: string | null): boolean {
  const na = a
    ?.trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');
  const nb = b
    ?.trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');
  if (!na || !nb) return false;
  return na === nb;
}

function findPageRecordForBasePath(pages: Page[], basePath: string): Page | undefined {
  const bp = basePath === '' ? '/' : basePath;
  if (bp === '/') {
    return pages.find(p => p.isHomePage) ?? pages[0];
  }
  const slug = bp.startsWith('/') ? bp.slice(1) : bp;
  return pages.find(p => p.slug === slug);
}

function pageHrefForSelect(pages: Page[], basePath: string): string {
  const page = findPageRecordForBasePath(pages, basePath);
  if (!page) {
    if (basePath === '/' || basePath === '') return '/';
    return basePath.startsWith('/') ? basePath : `/${basePath}`;
  }
  if (basePath === '/' || basePath === '' || page.isHomePage) return '/';
  return `/${page.slug}`;
}

/**
 * Format a component type into a display name
 */
const formatTypeName = (type: string): string => {
  return type
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Detect the current link mode based on the value
 */
const detectLinkMode = (
  value: string,
  pages: { slug: string }[],
  isLoadingPages?: boolean
): LinkMode => {
  if (!value) return 'page';

  // Internal deep link /path#section-* — use Page mode (page + optional section UI)
  if (
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.includes('://') &&
    value.includes('#')
  ) {
    const { sectionComponentId } = splitPageSectionHref(value);
    if (sectionComponentId !== null) {
      return 'page';
    }
    return 'custom';
  }

  // Check if it's a section link
  if (value === '#top' || value.startsWith('#section-')) {
    return 'section';
  }

  // Check if it matches a page
  if (value === '/' || value === '#') {
    return 'page';
  }

  // Check if value looks like a relative page path (starts with / but not http/https)
  const looksLikePagePath =
    value.startsWith('/') && !value.startsWith('//') && !value.includes('://');

  // If pages are still loading and value looks like a page path, assume it's a page
  if (isLoadingPages && looksLikePagePath) {
    return 'page';
  }

  const matchingPage = pages.find(page => value === `/${page.slug}` || value === page.slug);
  if (matchingPage) {
    return 'page';
  }

  // If pages are loaded and no match found, but it looks like a page path, still treat as page
  // This handles cases where the page might have been deleted but we still want to show the selector
  if (looksLikePagePath && !value.includes('.') && !value.includes('?')) {
    return 'page';
  }

  // Default to custom for external URLs or unmatched values
  return 'custom';
};

/**
 * Page selector component for URL selection
 * Allows selecting from existing pages, page sections, or entering a custom URL
 */
export const PageSelector: React.FC<PageSelectorProps> = ({
  value,
  onChange,
  pages,
  isLoadingPages,
  currentPageComponents,
}) => {
  const { currentPageId, currentPageSlug, layoutLinkHeaderComponents, layoutLinkFooterComponents } =
    useLinkTargetPageContext();

  const layoutExtrasPresent = !!(
    layoutLinkHeaderComponents?.length || layoutLinkFooterComponents?.length
  );

  // Detect initial mode based on the value
  const initialMode = useMemo(
    () => detectLinkMode(value, pages, isLoadingPages),
    // Only compute on mount, not on every value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [mode, setMode] = useState<LinkMode>(initialMode);

  const pageModeParsed = useMemo(() => {
    if (!value.startsWith('/') || value.startsWith('//') || value.includes('://')) {
      return { basePath: '/', sectionComponentId: null as string | null };
    }
    return splitPageSectionHref(value);
  }, [value]);

  const pageDropdownHref = useMemo(
    () => pageHrefForSelect(pages, pageModeParsed.basePath),
    [pages, pageModeParsed.basePath]
  );

  const targetPageId = useMemo(() => {
    if (mode !== 'page' || isLoadingPages || !value.startsWith('/')) return null;
    const p = findPageRecordForBasePath(pages, pageModeParsed.basePath);
    return p?.id ?? null;
  }, [mode, isLoadingPages, value, pages, pageModeParsed.basePath]);

  const targetPageSlug = useMemo(() => {
    if (mode !== 'page' || isLoadingPages || !value.startsWith('/')) return null;
    const p = findPageRecordForBasePath(pages, pageModeParsed.basePath);
    return p?.slug ?? null;
  }, [mode, isLoadingPages, value, pages, pageModeParsed.basePath]);

  const linkTargetMatchesEditorPage =
    samePageId(currentPageId ?? null, targetPageId ?? null) ||
    samePageSlug(currentPageSlug ?? null, targetPageSlug ?? null);

  const shouldMergeLayoutForTargetPage = layoutExtrasPresent && linkTargetMatchesEditorPage;

  const [targetLinkComponents, setTargetLinkComponents] = useState<Component[] | null>(null);
  const [targetLinkLoading, setTargetLinkLoading] = useState(false);
  const [targetLinkError, setTargetLinkError] = useState<string | null>(null);

  // Update mode when value changes externally (e.g., when editing different component)
  // Skip auto-detection for empty values since that's the expected state when switching to custom mode
  // Also re-detect when pages finish loading to correct any initial misdetection
  useEffect(() => {
    if (!value) return; // Don't change mode for empty values
    const detectedMode = detectLinkMode(value, pages, isLoadingPages);
    setMode(detectedMode);
  }, [value, pages, isLoadingPages]);

  useEffect(() => {
    if (!targetPageId || mode !== 'page') {
      setTargetLinkComponents(null);
      setTargetLinkLoading(false);
      setTargetLinkError(null);
      return;
    }

    if (linkTargetMatchesEditorPage && targetPageId && Array.isArray(currentPageComponents)) {
      setTargetLinkComponents(
        mergeBodyWithLayoutSections(
          currentPageComponents,
          layoutLinkHeaderComponents,
          layoutLinkFooterComponents,
          shouldMergeLayoutForTargetPage
        )
      );
      setTargetLinkLoading(false);
      setTargetLinkError(null);
      return;
    }

    let cancelled = false;
    setTargetLinkLoading(true);
    setTargetLinkError(null);

    componentsApi
      .getPageComponents(targetPageId)
      .then(res => {
        if (!cancelled) {
          setTargetLinkComponents(
            mergeBodyWithLayoutSections(
              res.components,
              layoutLinkHeaderComponents,
              layoutLinkFooterComponents,
              shouldMergeLayoutForTargetPage
            )
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTargetLinkError('Could not load sections for this page.');
          setTargetLinkComponents(null);
        }
      })
      .finally(() => {
        if (!cancelled) setTargetLinkLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    targetPageId,
    mode,
    currentPageId,
    currentPageSlug,
    currentPageComponents,
    layoutLinkHeaderComponents,
    layoutLinkFooterComponents,
    shouldMergeLayoutForTargetPage,
    linkTargetMatchesEditorPage,
  ]);

  // Get linkable components (visible, non-invisible types) including layout chrome in Section mode
  const linkableComponents = useMemo(() => {
    if (!currentPageComponents) return [];
    return mergeBodyWithLayoutSections(
      currentPageComponents,
      layoutLinkHeaderComponents,
      layoutLinkFooterComponents,
      layoutExtrasPresent
    );
  }, [
    currentPageComponents,
    layoutLinkHeaderComponents,
    layoutLinkFooterComponents,
    layoutExtrasPresent,
  ]);

  // Handle mode change
  const handleModeChange = (newMode: LinkMode) => {
    setMode(newMode);
    // Set appropriate default value for the new mode
    if (newMode === 'page') {
      onChange('/');
    } else if (newMode === 'section') {
      onChange('#top');
    } else {
      onChange('');
    }
  };

  // Render mode selector buttons
  const renderModeSelector = () => (
    <div className="flex rounded-lg border border-gray-300 p-0.5 mb-3 bg-gray-100">
      <button
        type="button"
        onClick={() => handleModeChange('page')}
        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
          mode === 'page'
            ? 'bg-white text-blue-700 shadow-sm border border-blue-300'
            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
        }`}
        title="Link to a page on your site"
      >
        <FileText size={14} />
        <span>Page</span>
      </button>
      <button
        type="button"
        onClick={() => handleModeChange('custom')}
        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
          mode === 'custom'
            ? 'bg-white text-blue-700 shadow-sm border border-blue-300'
            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
        }`}
        title="Enter a custom or external URL"
      >
        <ExternalLink size={14} />
        <span>External</span>
      </button>
      <button
        type="button"
        onClick={() => handleModeChange('section')}
        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
          mode === 'section'
            ? 'bg-white text-blue-700 shadow-sm border border-blue-300'
            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
        }`}
        title="Jump to a section on this page"
      >
        <Anchor size={14} />
        <span>Section</span>
      </button>
    </div>
  );

  // Render page mode content
  const renderPageMode = () => {
    const sectionIdForSelect = pageModeParsed.sectionComponentId || '';
    const linkableTargets = targetLinkComponents ?? [];

    return (
      <div className="space-y-3">
        <select
          value={pageDropdownHref}
          onChange={e => onChange(buildPageSectionHref(e.target.value, null))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          disabled={isLoadingPages}
        >
          <option value="/">{isLoadingPages ? 'Loading pages...' : 'Home'}</option>
          {pages.map(page => (
            <option key={page.id} value={`/${page.slug}`}>
              {page.title} (/{page.slug})
            </option>
          ))}
        </select>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Scroll to section (optional)
          </label>
          {targetLinkLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <Loader2 size={16} className="animate-spin" />
              Loading sections…
            </div>
          )}
          {targetLinkError && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              {targetLinkError}
            </p>
          )}
          {!targetLinkLoading &&
            !targetLinkError &&
            linkableTargets.length === 0 &&
            targetPageId && (
              <p className="text-xs text-gray-500">No linkable sections on this page.</p>
            )}
          {!targetLinkLoading && linkableTargets.length > 0 && (
            <select
              value={sectionIdForSelect}
              onChange={e =>
                onChange(buildPageSectionHref(pageDropdownHref, e.target.value || null))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Top of page</option>
              {linkableTargets.map(component => (
                <option key={component.id} value={component.id}>
                  {COMPONENT_DISPLAY_NAMES[component.type] || formatTypeName(component.type)}
                </option>
              ))}
            </select>
          )}
          <p className="mt-1.5 text-xs text-gray-500">
            Choose a page, then optionally jump to an HTML embed, form, or other section on that
            page.
          </p>
        </div>
      </div>
    );
  };

  // Render section mode content
  const renderSectionMode = () => {
    // If no components available, show message
    if (!currentPageComponents || linkableComponents.length === 0) {
      return (
        <div className="px-3 py-2 border border-gray-200 rounded-md bg-gray-50">
          <p className="text-sm text-gray-500">No sections available on this page to link to.</p>
        </div>
      );
    }

    return (
      <>
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="#top">Top of Page</option>
          {linkableComponents.map(component => (
            <option key={component.id} value={`#section-${component.id}`}>
              {COMPONENT_DISPLAY_NAMES[component.type] || formatTypeName(component.type)}
            </option>
          ))}
        </select>
        <p className="mt-1.5 text-xs text-gray-500">
          Clicking this button will scroll visitors to the selected section.
        </p>
      </>
    );
  };

  // Render custom mode content
  const renderCustomMode = () => (
    <>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        placeholder="https://example.com or /custom-path"
      />
      <p className="mt-1.5 text-xs text-gray-500">
        Enter a full URL (https://...), a path (/page-slug), or a deep link such as
        /page-slug#section-componentId to scroll to a section on another page.
      </p>
    </>
  );

  return (
    <div>
      {renderModeSelector()}
      {mode === 'page' && renderPageMode()}
      {mode === 'section' && renderSectionMode()}
      {mode === 'custom' && renderCustomMode()}
    </div>
  );
};
